// apps/client/src/world/spaces/out.ts (M0-T18) — the outdoor SpaceScene: M0-T13's OutdoorScene, lit by the town clock, walked on the
// shared nav grid with the same step rules the server checks (walkable tile, then canStep across a tile edge). Built once per town
// and kept: a resync or a return from indoors reconciles the objects instead of rebuilding the island.
import { tileOf, type ObjectId, type SpaceSnapshot, type TownId, type WorldObject } from '@whiskerton/shared';
import { canStep, isWalkablePos } from '@whiskerton/shared/map/nav';   // subpath-only (not in the barrel)
import { Lighting } from '../../engine/lighting';
import type { Game } from '../../game/game';
import type { SpaceScene, SpaceSceneDef } from '../../game/spaces';
import { useGame } from '../../state/store';
import { createOutdoorScene, type OutdoorScene } from '../outdoor';

type OutSpace = SpaceScene & { readonly townId: TownId };
const built = new WeakMap<Game, OutSpace>();
/** The latest outdoor build per game: a build overtaken by a newer one (a resync during the first asset load) is not kept. */
const builds = new WeakMap<Game, number>();
/** One Lighting per OutdoorScene for its whole life: re-added after every build (build() clears the scene), so overlapping builds
 *  never stack a second sun, hemisphere light and sky, and a new town reuses the same shadow map. */
const lights = new WeakMap<OutdoorScene, Lighting>();

function lightingOf(out: OutdoorScene): Lighting {
  const kept = lights.get(out);
  if (!kept) { const l = new Lighting(out.scene); lights.set(out, l); return l; }
  out.scene.add(kept.hemi, kept.sun, kept.sun.target, kept.sky);  // Object3D.add re-parents, so adding twice is harmless
  return kept;
}

/** Brings the outdoor scene in line with a fresh object list: new ids added, changed ones updated, missing ones removed. */
function reconcile(next: readonly WorldObject[], game: Game): void {
  const out = game.scenes.out;
  if (!out) return;
  const prev = new Map(game.objects().map((o) => [o.id, o]));
  const seen = new Set<ObjectId>();
  for (const o of next) {
    seen.add(o.id);
    const p = prev.get(o.id);
    if (!p) void out.addObject(o);
    else if (JSON.stringify(p) !== JSON.stringify(o)) out.updateObject(o);
  }
  for (const id of prev.keys()) if (!seen.has(id)) out.removeObject(id);
  game.setObjects(next);
  const you = useGame.getState().you;
  if (you) out.refreshPerResident(you);
}

async function build(snap: SpaceSnapshot, game: Game): Promise<SpaceScene> {
  if (snap.kind !== 'out') throw new Error('the outdoor space needs an out snapshot');
  const s = useGame.getState(), town = s.town, map = game.map;
  if (!town || !map) throw new Error('the outdoor space needs the town snapshot first');
  const kept = built.get(game);
  if (kept && kept.townId === town.id && game.scenes.out) { reconcile(snap.objects, game); return kept; }
  const out = game.scenes.out ?? createOutdoorScene();
  game.scenes.out = out;
  game.setObjects(snap.objects);                                 // before the await: object.* events during the build update it
  const token = (builds.get(game) ?? 0) + 1;
  builds.set(game, token);
  await out.build(map, snap.objects, { assets: game.assets, town, residents: s.residents, you: s.you, now: game.now(), heightAt: (p) => out.heightAt(p) });
  const lighting = lightingOf(out);                              // after build: build() clears the scene
  const space: OutSpace = {
    townId: town.id, scene: out.scene, bounds: { x: 0, y: 0, w: map.w, h: map.h },
    heightAt: (p) => out.heightAt(p),
    walkable: (p) => { const nav = game.nav(); return !!nav && isWalkablePos(nav, p); },
    canMove: (a, b) => {
      const nav = game.nav(), m = game.map;
      if (!nav || !m || !isWalkablePos(nav, b)) return false;
      const ta = tileOf(a), tb = tileOf(b);
      return (ta.x === tb.x && ta.y === tb.y) || canStep(nav, m, ta, tb);
    },
    tick: (dt, minute) => out.tick(dt, lighting.update(minute, game.focus())),
    // Kept for the whole visit. Leaving the town (SpaceManager.reset) forgets it, but keeps the OutdoorScene itself: the next
    // town builds into it, and OutdoorScene.build() frees the previous island's terrain, meshes and textures.
    dispose: () => { if (built.get(game) === space) built.delete(game); },
  };
  // Overtaken by a newer build (SpaceManager drops this result as superseded): only the latest build is kept for the visit.
  if (builds.get(game) === token) built.set(game, space);
  return space;
}

export const space: SpaceSceneDef = {
  kind: 'out',
  build,
  apply: (snap, _scene, game) => { if (snap.kind === 'out') reconcile(snap.objects, game); },
};
